import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, User, Phone, MapPin, AlertTriangle, X } from 'lucide-react';
import { toast } from 'react-toastify';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../hooks/useAuth';
import maintenanceService from '../../services/maintenanceService';
import UniformMaintenanceModal from '../../components/modals/UniformMaintenanceModal';

const Register = () => {
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    restaurantName: '',
    ownerName: '',
    email: '',
    phone: '',
    address: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkMaintenance = async () => {
      const status = await maintenanceService.refreshMaintenanceStatus();
      if (status.maintenanceType === 'partial') {
        setShowMaintenanceModal(true);
      }
    };
    checkMaintenance();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Vérifier le mode maintenance avant de procéder
    if (!maintenanceService.isRegistrationAllowed()) {
      setShowMaintenanceModal(true);
      return;
    }
    
    // Validation basique
    if (!formData.restaurantName || !formData.ownerName || !formData.email || !formData.phone || !formData.address || !formData.password || !formData.confirmPassword) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setIsLoading(true);
    try {
      const registerData = {
        email: formData.email,
        password: formData.password,
        restaurantName: formData.restaurantName,
        ownerName: formData.ownerName,
        phone: formData.phone,
        address: formData.address
      };
      
      await register(registerData);
      toast.success('Compte créé avec succès. Veuillez vérifier votre email');
      navigate('/login');
    } catch (error: any) {
      // Ne pas afficher de toast si l'erreur vient de la validation du mot de passe
      // car authService a déjà affiché le message approprié
      if (!error.message || (!error.message.includes('mot de passe') && !error.message.includes('caractère'))) {
        toast.error(error.message || 'Erreur lors de la création du compte');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="w-full max-w-lg"
      >
        <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-3xl shadow-2xl shadow-gray-900/5 p-10">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center mb-6">
              <Link to="/" className="transition-all duration-300 hover:scale-105 hover:opacity-80">
                <img 
                  src="/logo.png" 
                  alt="EspaceResto" 
                  className="h-12 w-auto cursor-pointer"
                />
              </Link>
            </div>
            <h2 className="text-2xl font-light text-gray-900 mb-3 tracking-tight">Créer un compte</h2>
            <p className="text-gray-500 font-light text-sm">Rejoignez notre plateforme de gestion</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <Input
                  label="Nom du restaurant"
                  type="text"
                  name="restaurantName"
                  value={formData.restaurantName}
                  onChange={handleInputChange}
                  placeholder="Le Petit Bistrot"
                  leftIcon={<User className="h-4 w-4" />}
                  required
                />
              </div>

              <div className="space-y-1">
                <Input
                  label="Nom du propriétaire"
                  type="text"
                  name="ownerName"
                  value={formData.ownerName}
                  onChange={handleInputChange}
                  placeholder="Jean Dupont"
                  leftIcon={<User className="h-4 w-4" />}
                  required
                />
              </div>
            </div>

            <div className="space-y-1">
              <Input
                label="Adresse email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="votre@email.com"
                leftIcon={<Mail className="h-4 w-4" />}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <Input
                  label="Téléphone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+33 1 23 45 67 89"
                  leftIcon={<Phone className="h-4 w-4" />}
                  required
                />
              </div>

              <div className="space-y-1">
                <Input
                  label="Adresse du restaurant"
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="123 Rue de la Paix, 75001 Paris"
                  leftIcon={<MapPin className="h-4 w-4" />}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <Input
                  label="Mot de passe"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  leftIcon={<Lock className="h-4 w-4" />}
                  rightIcon={showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  onRightIconClick={() => setShowPassword(!showPassword)}
                  required
                />
              </div>

              <div className="space-y-1">
                <Input
                  label="Confirmer le mot de passe"
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  leftIcon={<Lock className="h-4 w-4" />}
                  rightIcon={showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  onRightIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  required
                />
              </div>
            </div>

            <div className="pt-4">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  type="submit"
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white py-4 rounded-2xl font-light transition-all duration-300 hover:shadow-lg hover:shadow-gray-900/20"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Création...
                    </div>
                  ) : (
                    'Créer le compte'
                  )}
                </Button>
              </motion.div>
            </div>
          </form>

          <div className="mt-10 text-center">
            <p className="text-xs text-gray-500 font-light">
              Déjà un compte ?{' '}
              <button 
                onClick={() => navigate('/login')}
                className="text-gray-700 hover:text-gray-900 font-medium transition-colors duration-200 bg-transparent border-none cursor-pointer hover:underline"
              >
                Se connecter
              </button>
            </p>
          </div>
        </div>
      </motion.div>

      {/* Modal de maintenance */}
      <UniformMaintenanceModal
        isOpen={showMaintenanceModal}
        onClose={() => {
          setShowMaintenanceModal(false);
          navigate('/');
        }}
      />
    </div>
  );
};

export default Register;